import React from "react";
import {
  Grid,
  makeStyles,
  Typography,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Button,
  IconButton,
} from "@material-ui/core";
import {
  AddCircleOutline,
  FormatListNumbered,
  HighlightOff,
} from '@material-ui/icons/';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import useError from "Hooks/useError";
import MuiAlert from "@material-ui/lab/Alert";
import { message } from "constants/message";
import {
  MuiPickersUtilsProvider,
  DateTimePicker,
} from "@material-ui/pickers";
import ClearIcon from "@material-ui/icons/Clear";
import DateFnsUtils from "@date-io/date-fns";

import "dayjs/locale/ja";
import dayjs from "dayjs";
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.locale("ja");
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);


const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  wrapper: {
    marginTop: theme.spacing(2),
  },
  dateWrapper: {
    display: "flex",
    marginTop: theme.spacing(2),
  },
  selectFormControl: {
    width: "125px"
  },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(2),
  },
  clearButton: {
    width: "32px",
    height: "32px",
  },
  paramWrapper: {
    display: "flex",
    marginTop: theme.spacing(2),
  },
  messsageWrapper: {
    marginTop: theme.spacing(1),
  },
  buttonRed: {
    color: "rgb(220, 0, 78)",
    "&:hover": {
      backgroundColor: "rgba(220, 0, 78, 0.04)",
    },
  },
  urlParamsInput: {
    marginRight: theme.spacing(2),
  }
}));

// パラメータ入力フォーム出力制御
function UrlParams(props) {
  const classes = useStyles();

  const { 
    inputParams, 
    updateParamName, 
    updateParamValue, 
    addInputParam, 
    deleteInputParam, 
    index } = props;

  const paramName = inputParams[index][0];
  const paramValue = inputParams[index][1];

  return (
    <Grid item xs={12} className={classes.paramWrapper}>
      <TextValidator
        label="パラメータ名"
        className={classes.urlParamsInput}
        value={paramName}
        onChange={(e) => {
          updateParamName(e, index);
        }}
        type="text"
      />
      <TextValidator
        label="パラメータ値"
        value={paramValue}
        onChange={(e) => {
          updateParamValue(e, index);
        }}
        type="text"
      />
      {(Number(index) + 1) == inputParams.length && (
        <IconButton 
          color="secondary" 
          aria-label="add an AddCircleOutline"
          onClick={() => addInputParam()}
        >
          <AddCircleOutline />
        </IconButton>
      )}
      {index > 0 && (
        <IconButton 
          className={classes.buttonRed} 
          aria-label="add an HighlightOff"
          onClick={() => deleteInputParam(index)}
        >
          <HighlightOff />
        </IconButton>
      )}
    </Grid>
  );
}

// Update 2024.03.31 isEdit追加で複製・新規と更新を切り分ける
export default function Form({ informationUrl, submit, isEdit = false}) {
  const classes = useStyles();
  const stateError = useError();
  const messages = message();
  // フォームパラメータ
  const [validFrom, setValidFrom] = React.useState(null);
  const [validUntil, setValidUntil] = React.useState(null);
  const [title, setTitle] = React.useState("");
  const [desc, setDesc] = React.useState("");
  const [url, setUrl] = React.useState("");
  const [tId, setTId] = React.useState(false);
  const [bfEnv, setBfEnv] = React.useState(false);
  const [params, setParams] = React.useState({});
  // informationUrlの取得判定
  const [isGetInformationUrl, setIsGetInformationUrl] = React.useState(false);

  // input form 個数管理
  const [inputParams, setInputParams] = React.useState([['', '']]);

  React.useEffect(() => {
    stateError.initialize();
  }, []);

  React.useEffect(() => {
    if (!informationUrl) return;
    
    setInputParams([]);
    setValidFrom(informationUrl.validFrom);
    setValidUntil(informationUrl.validUntil);
    setTitle(informationUrl.title);
    setDesc(informationUrl.desc);
    setUrl(informationUrl.url);
    setTId(informationUrl.tId);
    setBfEnv(informationUrl.bfEnv);
    setParams(informationUrl.params);
    setIsGetInformationUrl(true);
  }, [informationUrl]);

  React.useEffect(() => {
    if (!isGetInformationUrl) return;
    
    let paramCount = 0;

    for (const key in params) {
      const newArray = [];
      inputParams.push([key, params[key]]);
      const updateInputParams = newArray.concat(inputParams);
      setInputParams(updateInputParams);

      paramCount++;
    };

    if (paramCount == 0) {
      addInputParam();
    }
  }, [params]);

  const dateValidation = (date, type) => {
    // 入力内容が日付として正しいかチェック
    if (dayjs(date, "YYYY/MM/DD HH:mm:ss").isValid()) {
      if (type == "validFrom") {
        const dateTime = dayjs(validUntil).format("YYYY-MM-DD HH:mm:ss")
        const selectedDateTime = dayjs(date).format("YYYY-MM-DD HH:mm:ss")
        // 比較対象がnullなため比較なしでset
        // 終了日付が開始日付より後の日かチェック
        if (!dayjs(dateTime).isValid() || (dayjs(dateTime).isSameOrAfter(dayjs(selectedDateTime)))) {
          setValidFrom(dayjs(date).format("YYYY-MM-DD HH:mm:ss"));
        } else {
          alert(messages.validation.dateRang);
        }
      } else if (type == "validUntil"){
        const dateTime = dayjs(validFrom).format("YYYY-MM-DD HH:mm:ss")
        const selectedDateTime = dayjs(date).format("YYYY-MM-DD HH:mm:ss")
        // 比較対象がnullなため比較なしでset
        // 開始日付が終了日付より前の日かチェック
        if (!dayjs(dateTime).isValid() || (dayjs(dateTime).isSameOrBefore(dayjs(selectedDateTime)))) {
          setValidUntil(dayjs(date).format("YYYY-MM-DD HH:mm:ss"));
        } else {
          alert(messages.validation.dateRang);
        }
      }
      return;
    }
    return;
  }

  function clearValidFrom(e) {
    e.stopPropagation();
    setValidFrom(null);
  }

  function clearValidUntil(e) {
    e.stopPropagation();
    setValidUntil(null);
  }

  // 入力されたパラメータ名を更新
  function updateParamName(event, index) {
    event.preventDefault();
    const newArray = [];
    inputParams[index][0] = event.target.value;
    const updateInputParams = newArray.concat(inputParams);
    setInputParams(updateInputParams);
  }

  // 入力されたパラメータ値を更新
  function updateParamValue(event, index) {
    event.preventDefault();
    const newArray = [];
    inputParams[index][1] = event.target.value;
    const updateInputParams = newArray.concat(inputParams);
    setInputParams(updateInputParams);
  }

  function addInputParam() {
    inputParams.push(['', '']);
    const newArray = [];
    const updateInputParams = newArray.concat(inputParams);
    setInputParams(updateInputParams);
  }

  function deleteInputParam(paramIndex) {
    inputParams.splice(paramIndex,1);
    const newArray = [];
    const updateInputParams = newArray.concat(inputParams);
    setInputParams(updateInputParams);
  }

  const onError = () => {
    stateError.dispatch({
      type: "set",
      payload: {
        isError: true,
        errorMsg: messages.validation.common,
      },
    });
  };

  return (
    <Grid>
      <ValidatorForm
        onSubmit={(e) => {
          e.preventDefault();
          submit(
            { 
              validFrom,
              validUntil,
              title,
              desc,
              url,
              tId,
              bfEnv,
              inputParams,
            }
          );
        }}
        onError={onError}
      >
        <Paper elevation={3} className={classes.paper}>
          <Typography variant="h6" color="primary">
            {isEdit ? "ヘッダー告知更新" : "ヘッダー告知登録"}
          </Typography>
          <Grid container>
            <Grid item xs={12} className={classes.dateWrapper}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DateTimePicker
                  label="掲載開始日時"
                  format="yyyy/MM/dd HH:mm:ss"
                  value={validFrom}
                  onChange={(date) => dateValidation(date, "validFrom")}
                  InputProps={{
                    endAdornment: (
                      <IconButton 
                        className={classes.clearButton} 
                        onClick={(e) => clearValidFrom(e)}>
                          <ClearIcon />
                      </IconButton>
                    )
                  }}
                />
              </MuiPickersUtilsProvider>
              <div className={classes.divider}>〜</div>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DateTimePicker
                  label="掲載終了日時"
                  format="yyyy/MM/dd HH:mm:ss"
                  value={validUntil}
                  onChange={(date) => dateValidation(date, "validUntil")}
                  InputProps={{
                    endAdornment: (
                      <IconButton 
                        className={classes.clearButton} 
                        onClick={(e) => clearValidUntil(e)}>
                          <ClearIcon />
                      </IconButton>
                    )
                  }}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
              <TextValidator
                label="タイトル*"
                fullWidth
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                }}
                type="text"
                validators={['required']}
                errorMessages={[
                  messages.validation.required, 
                ]}
              />
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
              <TextValidator
                label="概要*"
                fullWidth
                value={desc}
                onChange={(event) => {
                  setDesc(event.target.value);
                }}
                type="text"
                validators={['required']}
                errorMessages={[
                  messages.validation.required, 
                ]}
              />
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
              <TextValidator
                label="URL*"
                fullWidth
                value={url}
                onChange={(event) => {
                  setUrl(event.target.value);
                }}
                type="text"
                validators={['required']}
                errorMessages={[
                  messages.validation.required, 
                ]}
              />
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
              <FormControl component="fieldset">
                <FormLabel>オプション</FormLabel>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={tId} 
                        onChange={(event) => {
                          setTId(event.target.checked)
                        }} 
                        name="t_id" 
                      />
                    }
                    label="t_id"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={bfEnv} 
                        onChange={(event) => {
                          setBfEnv(event.target.checked)
                        }} 
                        name="bf_env" 
                      />}
                    label="bf_env"
                  />
                </FormGroup>
              </FormControl>
            </Grid>
            {inputParams.map( 
              function (inputParam, index) {
                return <UrlParams 
                        inputParams={inputParams} 
                        updateParamName={updateParamName} 
                        updateParamValue={updateParamValue} 
                        addInputParam={addInputParam} 
                        deleteInputParam={deleteInputParam} 
                        index={index} 
                       />
              } 
            )}
          </Grid>
        </Paper>
        {stateError.state.isError && (
          <div className={classes.messsageWrapper}>
            <MuiAlert
              elevation={6}
              variant="filled"
              severity="error"
              className={classes.toast}
            >
              {stateError.state.errorMsg}
            </MuiAlert>
          </div>
        )}
        <Grid container justifyContent="center" className={classes.wrapper}>
          <Button type="submit" variant="contained" color="primary">
          {isEdit ? "更新" : "登録"}
          </Button>
        </Grid>
      </ValidatorForm>
    </Grid>
  );
}
